
import { useRef, useEffect, useState } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import Message from "../Message";
import SuggestedQuestions from "./SuggestedQuestions";
import DynamicStatusIndicator from "./DynamicStatusIndicator";
import { Message as MessageType } from "@/types";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLocation, useNavigate } from "react-router-dom";

interface MessagesListProps {
  messages: MessageType[];
  isProcessing: boolean;
  suggestedQuestions: string[];
  onSelectQuestion: (question: string) => void;
  hasUploadedFile?: boolean;
}

const MessagesList: React.FC<MessagesListProps> = ({ 
  messages, 
  isProcessing, 
  suggestedQuestions,
  onSelectQuestion,
  hasUploadedFile = false
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();
  const [enableStreamingForLastMessage, setEnableStreamingForLastMessage] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);

  // Enhanced scroll function that ensures complete visibility
  const scrollToBottom = (behavior: ScrollBehavior = "smooth") => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ 
        behavior, 
        block: "end",
        inline: "nearest"
      });
    }
  };

  // Scroll when messages change or when streaming starts/stops
  useEffect(() => {
    scrollToBottom();
  }, [messages.length]);

  // Enhanced streaming detection and scroll management
  useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      
      // Detectar streaming real baseado em metadata
      const isRealTimeStreaming = lastMessage.metadata?.isStreaming;
      
      if (lastMessage.role === "assistant") {
        if (isRealTimeStreaming) {
          setIsStreaming(true);
          setEnableStreamingForLastMessage(false); // Desabilitar simulação
          scrollToBottom("auto");
        } else if (!isProcessing) {
          setEnableStreamingForLastMessage(true);
          setIsStreaming(true);
          
          // Scroll immediately when streaming starts
          scrollToBottom("smooth");
          
          const timer = setTimeout(() => {
            setEnableStreamingForLastMessage(false);
            setIsStreaming(false);
            // Final scroll when streaming completes
            setTimeout(() => scrollToBottom("smooth"), 100);
          }, 100);
          return () => clearTimeout(timer);
        }
      }
    }
  }, [messages.length, isProcessing]);

  // Continuous scroll during streaming
  useEffect(() => {
    if (isStreaming) {
      const scrollInterval = setInterval(() => {
        scrollToBottom("auto");
      }, 500);
      
      return () => clearInterval(scrollInterval);
    }
  }, [isStreaming]);

  // Scroll when processing status changes
  useEffect(() => {
    if (isProcessing) {
      setTimeout(() => scrollToBottom("smooth"), 100);
    }
  }, [isProcessing]);

  // Handle browser navigation events
  useEffect(() => {
    const handlePopState = () => {
      console.log("Navigation event detected:", window.location.pathname);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [navigate]);

  return (
    <ScrollArea className="flex-1 p-2 sm:p-3 md:p-4" ref={scrollAreaRef}>
      <div className="space-y-4 md:space-y-6 pb-4 md:pb-6">
        {messages.map((message, index) => (
          <Message 
            key={message.metadata?.client_msg_id || message.id} 
            message={message} 
            isLastMessage={index === messages.length - 1}
            enableStreaming={index === messages.length - 1 && enableStreamingForLastMessage}
          />
        ))}
        
        {isProcessing && (
          messages.length > 0 && (
            messages[messages.length - 1]?.content?.toLowerCase().includes('jurisprudência') ||
            messages[messages.length - 1]?.content?.toLowerCase().includes('jurisprudencia') ||
            messages[messages.length - 1]?.content?.toLowerCase().includes('tem alguma jurisprudencia')
          ) ? (
            <div className="flex items-center justify-center py-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <div className="loader"></div>
                <span>🔍 Buscando jurisprudência...</span>
              </div>
            </div>
          ) : (
            <DynamicStatusIndicator 
              isActive={isProcessing} 
              hasFile={hasUploadedFile}
            />
          )
        )}
        
        {!isProcessing && suggestedQuestions.length > 0 && (
          <SuggestedQuestions 
            questions={suggestedQuestions} 
            onSelectQuestion={onSelectQuestion} 
          />
        )}
        
        <div ref={messagesEndRef} style={{ height: '20px' }} />
      </div>
    </ScrollArea>
  );
};

export default MessagesList;
